import React, { createContext, useContext } from "react";
import { auth } from "../firebase";

/**
 * Identidad "efectiva" para la vista de residente.
 *
 * POR QUÉ: las pantallas de residente resuelven "quién soy" leyendo
 * `auth.currentUser.email` directo y consultando `users where email==`. En el
 * modo "Ver como residente" (herramienta de admin) el admin NO cambia de sesión
 * de Firebase — sigue logueado como admin — así que hay que interceptar ese
 * "quién soy" para que las pantallas usen el email del residente impersonado.
 *
 * Uso en pantallas de residente:
 *   const { email } = useEffectiveIdentity();   // en vez de auth.currentUser.email
 * y el resto (users where email==, sacar userid) queda igual.
 *
 * Uso para bloquear acciones (solo lectura):
 *   const readOnly = useIsReadOnly();            // true cuando se está "viendo como"
 */

const Ctx = createContext(null);

/**
 * Envuelve la vista de residente SOLO en modo "ver como".
 * value: { email, userid, readOnly: true, viewingAs: { name, unitLabel } }
 */
export function EffectiveIdentityProvider({ value, children }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Devuelve { email, userid, readOnly, viewingAs }.
 * - En "ver como" → datos del residente impersonado + readOnly:true.
 * - Normal → el email del usuario logueado (userid lo resuelve la pantalla como hoy).
 */
export function useEffectiveIdentity() {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  return {
    email: (auth.currentUser?.email || "").toLowerCase(),
    userid: null,
    readOnly: false,
    viewingAs: null,
  };
}

/** Atajo para gatear acciones de escritura: true cuando se está "viendo como" (solo lectura). */
export function useIsReadOnly() {
  return !!useContext(Ctx)?.readOnly;
}
